import { useState, useEffect } from 'react';
import API from '../utils/api';

export default function BillingSettings({ user }) {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [billingAddress, setBillingAddress] = useState({
    fullName: user?.firstName + ' ' + user?.lastName || '',
    email: user?.email || '',
    street: '',
    city: user?.locationCity || '',
    state: user?.locationState || '',
    zip: user?.locationZip || '',
    country: 'US',
  });
  const [editMode, setEditMode] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      const { data } = await API.get('/billing/payment-methods');
      setPaymentMethods(data.paymentMethods || []);
    } catch (err) {
      console.error('Failed to load payment methods:', err);
    }
  };

  const handleAddPaymentMethod = () => {
    // This would open a Stripe Elements form or redirect to payment setup
    alert('Payment method management will use Stripe Elements for secure credit card entry.\n\nThis feature will handle:\n• Secure credit card tokenization\n• Multiple payment methods\n• Set default payment method\n• Delete payment methods');
  };

  const handleSaveBillingAddress = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setSuccess('');

    // Validate required fields
    if (!billingAddress.city || !billingAddress.state || !billingAddress.zip) {
      setErrors({ submit: 'City, State, and Zip are required' });
      setLoading(false);
      return;
    }

    try {
      await API.patch('/billing/address', billingAddress);
      setSuccess('✅ Billing address updated');
      setEditMode(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setErrors({ submit: err.response?.data?.error || 'Failed to update address' });
    } finally {
      setLoading(false);
    }
  };

  const f = (name) => ({
    className: `form-input ${errors[name] ? 'error' : ''}`,
    value: billingAddress[name],
    onChange: e => setBillingAddress(p => ({ ...p, [name]: e.target.value })),
  });

  return (
    <div>
      <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 700, marginBottom: 24 }}>
        💳 Billing & Payment
      </h3>

      {/* Payment Methods */}
      <div style={{ background: 'white', borderRadius: 14, border: '1px solid var(--border)', padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h4 style={{ fontSize: 16, fontWeight: 700 }}>💰 Payment Methods</h4>
          <button className="btn btn-cherry btn-sm" onClick={handleAddPaymentMethod}>
            + Add Payment Method
          </button>
        </div>

        {paymentMethods.length === 0 ? (
          <div style={{ padding: 24, background: 'var(--cream)', borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: 'var(--text-soft)', marginBottom: 12 }}>
              No payment methods saved yet
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-soft)', lineHeight: 1.6 }}>
              Add a credit or debit card to make purchases and bid on auctions. Your payment information is securely processed through Stripe.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {paymentMethods.map(pm => (
              <div key={pm.id} style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                    💳 {pm.brand} ending in {pm.last4}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>
                    Expires {pm.expMonth}/{pm.expYear}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {pm.isDefault && (
                    <span style={{ fontSize: 11, fontWeight: 700, background: '#EAF5EC', color: '#2D7A3A', padding: '4px 12px', borderRadius: 100 }}>
                      Default
                    </span>
                  )}
                  <button className="btn btn-ghost btn-sm" onClick={() => alert('Delete payment method')}>
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Billing Address */}
      <div style={{ background: 'white', borderRadius: 14, border: '1px solid var(--border)', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h4 style={{ fontSize: 16, fontWeight: 700 }}>📍 Billing Address</h4>
          {!editMode && (
            <button className="btn btn-ghost btn-sm" onClick={() => setEditMode(true)}>
              ✏️ Edit
            </button>
          )}
        </div>

        {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div>}
        {errors.submit && <div className="alert alert-error" style={{ marginBottom: 16 }}>{errors.submit}</div>}

        {editMode ? (
          <form onSubmit={handleSaveBillingAddress}>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Full Name<span className="required"> *</span></label>
              <input {...f('fullName')} required />
            </div>

            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Email<span className="required"> *</span></label>
              <input {...f('email')} type="email" required />
            </div>

            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Street Address<span className="required"> *</span></label>
              <input {...f('street')} placeholder="123 Main St" required />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group">
                <label className="form-label">City<span className="required"> *</span></label>
                <input {...f('city')} required />
              </div>
              <div className="form-group">
                <label className="form-label">State<span className="required"> *</span></label>
                <input {...f('state')} placeholder="CA" required />
              </div>
              <div className="form-group">
                <label className="form-label">Zip<span className="required"> *</span></label>
                <input {...f('zip')} required />
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="form-label">Country</label>
              <select
                className="form-input"
                value={billingAddress.country}
                onChange={e => setBillingAddress(p => ({ ...p, country: e.target.value }))}
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="MX">Mexico</option>
                <option value="GB">United Kingdom</option>
                <option value="AU">Australia</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button type="submit" className="btn btn-cherry" disabled={loading}>
                {loading ? <><span className="spinner" /> Saving...</> : '💾 Save Address'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setEditMode(false)}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div style={{ padding: 16, background: 'var(--cream)', borderRadius: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{billingAddress.fullName}</div>
            <div style={{ fontSize: 13, color: 'var(--text-soft)', lineHeight: 1.6 }}>
              {billingAddress.street && <div>{billingAddress.street}</div>}
              {billingAddress.city && (
                <div>
                  {billingAddress.city}, {billingAddress.state} {billingAddress.zip}
                </div>
              )}
              <div>{billingAddress.country}</div>
              <div style={{ marginTop: 8, fontSize: 12 }}>{billingAddress.email}</div>
            </div>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div style={{ marginTop: 32, padding: 20, background: '#EBF0FF', borderRadius: 12, border: '1px solid #1A5276' }}>
        <div style={{ fontSize: 13, color: '#1A5276', lineHeight: 1.8 }}>
          <strong>🔒 Payment Security:</strong> All payments are processed securely through Stripe. We never store your full credit card information—only a secure token. Your billing information is encrypted and PCI-DSS compliant.
        </div>
      </div>
    </div>
  );
}
